import React, { createContext, useContext } from 'react';
import { buildParentIndex, rootBlockIdOf } from '../utils/brokenLinks/blockIndex';
import { VARIABLE_CLASS } from '../utils/variableNodes';
import { requestVariableFocus } from '../utils/variableFocusRegistry';

/**
 * Blocs verrouillés par le modèle — pendant web des « Lignes bloquées » de
 * l'éditeur classique (`container.editable === false`).
 *
 * Le verrou se pose dans le modèle (`locked` sur le Bloc) et ne s'applique que
 * sur le site, comme dans le classique :
 * - le Bloc garde son déplacement et sa suppression, perd ses réglages ;
 * - ses box ne s'éditent, ne se déplacent ni ne se suppriment, et aucune box
 *   ne s'y ajoute ;
 * - les images restent remplaçables ;
 * - une variable de texte garde son survol et, au double-clic, ouvre le
 *   formulaire sur elle, comme une variable d'une ligne bloquée du classique.
 */

/** 'template' : on pose les verrous ; 'site' : on les subit. */
export const TemplateLockContext = createContext(null);

export const useTemplateLockMode = () => useContext(TemplateLockContext);

/** Les box qu'un verrou laisse remplacer, comme les images du classique. */
export const LOCK_EXEMPT_TYPES = ['ProposalImage'];

/** Le Bloc verrouillé qui contient `blockId` (lui compris), sinon null. */
export function lockingSection(data, blockId) {
  if (!data || !blockId) return null;
  const rootId = rootBlockIdOf(String(blockId), buildParentIndex(data));
  const root = (data.content || []).find((item) => String(item?.props?.id) === rootId);
  return root?.type === 'ProposalSection' && root.props?.locked ? root : null;
}

/** Les permissions d'un bloc sur le site, une fois les verrous du modèle appliqués. */
export function templateLockPermissions(item, data, inherited = {}) {
  const section = lockingSection(data, item?.props?.id);
  if (!section) return inherited;
  if (String(section.props.id) === String(item?.props?.id)) {
    return { ...inherited, edit: false };
  }
  return {
    ...inherited,
    drag: false,
    delete: false,
    duplicate: false,
    edit: LOCK_EXEMPT_TYPES.includes(item?.type) ? inherited.edit : false,
  };
}

/* Champ « Actions » posé par withBoxActions : seul à rester sur un élément
   figé, pour qu'on puisse encore le copier, ou supprimer le Bloc. */
const ACTIONS_KEY = '_actions';
const actionsOnlyCache = new WeakMap();

/* `edit: false` ne fige pas nos champs personnalisés (disposition, couleurs…),
   qui ignorent le `readOnly` de Puck : un élément figé n'en montre donc aucun.
   Même objet pour les mêmes champs, sinon l'inspecteur se reconstruirait. */
function actionsOnly(fields) {
  if (!fields?.[ACTIONS_KEY]) return {};
  if (!actionsOnlyCache.has(fields)) actionsOnlyCache.set(fields, { [ACTIONS_KEY]: fields[ACTIONS_KEY] });
  return actionsOnlyCache.get(fields);
}

/** Les champs d'un élément sur le site : ses actions seules s'il est figé. */
export function templateLockFields(item, data, fields) {
  return templateLockPermissions(item, data).edit === false ? actionsOnly(fields) : fields;
}

const variableOf = (event) => event.target?.closest?.(`.${VARIABLE_CLASS}`);

/* Sur une variable, le geste s'arrête au Bloc : l'éditeur de texte ne doit pas
   le recevoir, sinon il sélectionnerait la variable, qu'une frappe effacerait,
   et Puck sélectionnerait la box. */
const stopOnVariable = (event) => {
  if (!variableOf(event)) return;
  event.preventDefault();
  event.stopPropagation();
};

const focusVariableOnDoubleClick = (event) => {
  const variable = variableOf(event);
  if (!variable) return;
  stopOnVariable(event);
  requestVariableFocus(variable.getAttribute('data-variable-key'));
};

/** Les gestes encore permis dans un Bloc verrouillé : ceux des variables. */
const LOCKED_BLOC_HANDLERS = {
  onPointerDownCapture: stopOnVariable,
  onMouseDownCapture: stopOnVariable,
  onClickCapture: stopOnVariable,
  onDoubleClickCapture: focusVariableOnDoubleClick,
};

/**
 * Applique les verrous du modèle à la config Puck du site : permissions, champs, et
 * marqueurs DOM qui coupent le pointeur dans un Bloc verrouillé (Puck rend ses
 * box cliquables en `!important`, seule une règle CSS ciblée les neutralise —
 * cf. PUCK_CANVAS_FIX_CSS).
 */
export function withTemplateLocks(components) {
  return Object.fromEntries(
    Object.entries(components).map(([name, def]) => {
      if (!def) return [name, def];
      const Original = def.render;
      const marker = name === 'ProposalSection' ? 'data-template-locked' : null;
      const exempt = LOCK_EXEMPT_TYPES.includes(name);

      return [
        name,
        {
          ...def,
          resolvePermissions: async (data, params) => {
            const inherited = def.resolvePermissions ? await def.resolvePermissions(data, params) : {};
            return templateLockPermissions(data, params?.appState?.data, inherited);
          },
          resolveFields: (data, params) => {
            const resolved = def.resolveFields ? def.resolveFields(data, params) : params?.fields;
            const lock = (fields) => templateLockFields(data, params?.appState?.data, fields);
            return typeof resolved?.then === 'function' ? resolved.then(lock) : lock(resolved);
          },
          ...(typeof Original === 'function' && (marker || exempt) ? {
            render: (props) => {
              const attr = marker
                ? (props.locked && { [marker]: '', ...LOCKED_BLOC_HANDLERS })
                : { 'data-lock-exempt': '' };
              if (!attr) return <Original {...props} />;
              /* `display: contents` : la chaîne flex des colonnes ne doit pas
                 voir d'élément de plus. */
              return <div {...attr} style={{ display: 'contents' }}><Original {...props} /></div>;
            },
          } : {}),
        },
      ];
    }),
  );
}
